/**
 * D868 (owner decision 2026-10-31: "como super admin global vale para tudo no sistema") — the console's mirror of the one
 * exception to four eyes.
 *
 * An operator holding an active GLOBAL `SUPER_ADMIN` grant that no access profile narrows may approve, decide or publish
 * their OWN proposal. Every screen that hides or disables a decision because "the proposer is me" asks `maySelfApprove`
 * instead of hiding it for that operator; everyone else keeps four eyes. Display only: the server re-checks the role on
 * the session (`selfApprovalOf`) and the database accepts the reason code only for a super administrator (m0295).
 *
 * What this NEVER relaxes: the rules where the operator is the SUBJECT or the BENEFICIARY of the record (a payout to
 * oneself, one's own cash handover as the courier, an adjustment of one's own rider or driver account). Screens keep
 * those checks as they are.
 *
 * The predicate is `isGlobalSuperAdmin` of `@vello/rbac`, applied to the session's grants: the console's grants are the
 * wire shape (`OperatorGrant`, a `role` string and a profile reference), not the server's typed `AdminGrant`, so the
 * same three-field test is spelled here and pinned against the shared one by `self-approval.test.ts`.
 */
import { type OperatorGrant } from '@vello/api-client';
import { isSelfApproval, type SelfApproval } from '@vello/rbac';
import { type KnownGrants } from './console-access.js';

export { SUPER_ADMIN_SELF_APPROVAL, type SelfApproval } from '@vello/rbac';

const isGlobalSuperAdminGrant = (g: Pick<OperatorGrant, 'role' | 'scopeType' | 'profile'>): boolean => g.role === 'SUPER_ADMIN' && g.scopeType === 'GLOBAL' && g.profile === undefined;

/**
 * Whether these grants let the operator decide their own proposals. Unknown grants (the read has not answered or failed)
 * answer `false`: the console keeps hiding the decision, as before, rather than offer what the server may refuse.
 */
export function maySelfApprove(grants: KnownGrants): boolean {
  return grants !== undefined && grants.some(isGlobalSuperAdminGrant);
}

/**
 * The self-approval a decided record carries (`selfApproval: 'SUPER_ADMIN_SELF_APPROVAL'`), or undefined. Read defensively:
 * a record of an older server, or of a flow that did not record it, simply has none.
 */
export function selfApprovalOf(record: unknown): SelfApproval | undefined {
  if (typeof record !== 'object' || record === null) return undefined;
  const value = (record as { readonly selfApproval?: unknown }).selfApproval;
  return isSelfApproval(value) ? value : undefined;
}
